import React from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import Notification from 'rc-notification';
import styled from 'styled-components';
import { ThemeProps } from '../prop-types';
import { paddingLeft } from '../theme/directions';
import {
  colorBlackBackground,
  colorBlue,
  colorText,
  colorWhite
} from '../theme/palette';

let notification = null;
let notificationInitStarted = false;
const pendingNotices = [];

const applyHostContainerStyles = (container) => {
  // Host sem classe rc-notification: se crescer, bloqueia cliques na tela.
  // Ocupa a viewport so para ancorar os toasts, sem capturar pointer.
  container.style.position = 'fixed';
  container.style.inset = '0';
  container.style.width = '100%';
  container.style.height = '100%';
  container.style.margin = '0';
  container.style.padding = '0';
  container.style.border = '0';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '2000';
  container.setAttribute('data-spark-notification-host', 'true');
};

// React 19 removeu ReactDOM.render; rc-util ainda busca createRoot em react-dom
// (não em react-dom/client), então newInstance nunca chama o callback e a
// instância fica null. Inicializamos com createRoot diretamente.
const initNotificationInstance = () => {
  if (typeof document === 'undefined' || notification || notificationInitStarted) {
    return;
  }

  notificationInitStarted = true;
  const container = document.createElement('div');
  applyHostContainerStyles(container);
  document.body.appendChild(container);
  const root = createRoot(container);

  root.render(
    React.createElement(Notification, {
      maxCount: 3,
      // Sem motion: evita nós de leave fantasma (React 19 + rc-motion)
      // que continuam ocupando altura e empurram o próximo toast para baixo.
      animation: '',
      style: {
        top: 60,
        right: 0,
        left: 'auto',
        bottom: 'auto'
      },
      ref: (instance) => {
        if (!instance) {
          return;
        }
        notification = {
          notice: (noticeProps) => {
            instance.add(noticeProps);
          },
          removeNotice: (key) => {
            instance.remove(key);
          },
          destroy: () => {
            root.unmount();
            if (container.parentNode) {
              container.parentNode.removeChild(container);
            }
            notification = null;
            notificationInitStarted = false;
          }
        };

        while (pendingNotices.length > 0) {
          notification.notice(pendingNotices.shift());
        }
      }
    })
  );
};

initNotificationInstance();

const showNotification = (theme, rtl, title, message, type = 'danger') => {
  const notificationInitialProps = {
    content: <BasicNotification color={type} title={title} message={message} theme={theme} />,
    closable: true,
    duration: 5,
    style: {
      pointerEvents: 'auto'
    },
    className: `right-up ${rtl.direction}-support`
  };

  if (!notification) {
    initNotificationInstance();
    pendingNotices.push(notificationInitialProps);
    return;
  }

  notification.notice(notificationInitialProps);
};

const BasicNotification = ({
  color,
  title,
  message,
  theme
}) => <NotificationContent color={color} theme={theme.className}>
    <NotificationMessageWrap>
      <NotificationTitle>{title}</NotificationTitle>
      <NotificationMessage>{message}</NotificationMessage>
    </NotificationMessageWrap>
  </NotificationContent>;

BasicNotification.propTypes = {
  theme: ThemeProps.isRequired,
  color: PropTypes.string,
  title: PropTypes.string,
  message: PropTypes.string.isRequired
};
BasicNotification.defaultProps = {
  color: '',
  title: ''
};

const ImageNotification = ({
  img,
  title,
  message,
  theme
}) => <NotificationContent theme={theme.className}>
    <NotificationImage>
      <img src={img} alt="" />
    </NotificationImage>
    <NotificationMessageWrap>
      <NotificationTitle>{title}</NotificationTitle>
      <NotificationMessage>{message}</NotificationMessage>
    </NotificationMessageWrap>
  </NotificationContent>;

ImageNotification.propTypes = {
  theme: ThemeProps.isRequired,
  img: PropTypes.string.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired
};
ImageNotification.defaultProps = {
  title: ''
};

const FullWideNotification = ({
  color,
  message
}) => <NotificationContent fullWidth color={color}>
    <NotificationMessage>{message}</NotificationMessage>
  </NotificationContent>;

FullWideNotification.propTypes = {
  color: PropTypes.string,
  message: PropTypes.string.isRequired
};
FullWideNotification.defaultProps = {
  color: ''
};
export { BasicNotification, ImageNotification, FullWideNotification, showNotification }; // region STYLES

const NotificationMessage = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  font-size: 13px;
  line-height: 1.4;
  color: inherit;
`;
const NotificationTitle = styled.h6`
  margin-bottom: 4px;
  margin-top: 0;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
  color: inherit;
`;
const NotificationImage = styled.div`
  height: 106px;
  width: 106px;
  overflow: hidden;
  flex-shrink: 0;

  & ~ * {
    ${paddingLeft}: 106px;
  }
`;

const getColor = color => {
  switch (color) {
    case 'light':
      return colorWhite;

    case 'dark':
      return colorBlackBackground;

    case 'primary':
      return '#f8f9fa';

    case 'success':
      return '#f0f9f4';

    case 'warning':
      return '#fffbeb';

    case 'danger':
      return '#fef2f2';

    default:
      return colorWhite;
  }
};

const getBorderColor = color => {
  switch (color) {
    case 'primary':
      return colorBlue;

    case 'success':
      return '#10b981';

    case 'warning':
      return '#f59e0b';

    case 'danger':
      return '#ef4444';

    default:
      return '#e5e7eb';
  }
};

const getTextColor = color => {
  switch (color) {
    case 'primary':
      return '#1e40af';

    case 'success':
      return '#065f46';

    case 'warning':
      return '#92400e';

    case 'danger':
      return '#991b1b';

    default:
      return colorText;
  }
};

const NotificationMessageWrap = styled.div`
  padding: 12px 35px 12px 16px;
  flex: 1;
`;
const NotificationContent = styled.div`
  max-width: 320px;
  min-width: 280px;
  width: auto;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.1);
  background: ${props => getColor(props.color || props.theme)};
  position: relative;
  margin: 8px 16px;
  display: flex;
  border-radius: 6px;
  border-left: 4px solid ${props => getBorderColor(props.color)};
  overflow: hidden;

  ${props => props.fullWidth && `
    max-width: 100vw;
    width: 100vw;
    margin: 0;
    padding: 20px 40px 20px 25px;

    ${NotificationMessage} {
      text-align: center;
      width: calc(100% - 30px);
    }
  `}

  ${NotificationMessage}, ${NotificationTitle} {
    color: ${props => getTextColor(props.color)};
  }
`; // endregion
